import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  AreaChart, Area, ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { pilgrimGuidance, pilgrimAdvisories, missingPersonData } from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, STATUS_KEY, statusClasses } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LivePilgrimSection } from "../components/live/LiveResponseSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  MessageCircle, Phone, Mail, Headphones, MapPin, Shield,
  CheckCircle, Clock, Brain, Globe, Mic, Radio, Send,
  Users, AlertTriangle, Navigation, Star, TrendingUp, Zap,
  MessageSquare, Volume2, Smartphone, RefreshCw, Heart,
  ChevronDown, ChevronUp, Eye, Wifi
} from "lucide-react";

// ── inline data ───────────────────────────────────────────────────
const CHANNEL_DETAIL = [
  {
    key:"whatsapp", label:"WhatsApp", Icon:MessageCircle,
    requests:18640, resolved:17820, queued:82, avgWait:"12s",
    uptime:99.8, satisfaction:4.7,
    color:"text-emerald-600", bg:"bg-emerald-50", border:"border-emerald-200", fill:"#10B981",
  },
  {
    key:"sms", label:"SMS / Text", Icon:Mail,
    requests:12380, resolved:12240, queued:140, avgWait:"45s",
    uptime:99.9, satisfaction:4.4,
    color:"text-blue-600", bg:"bg-blue-50", border:"border-blue-200", fill:"#2563EB",
  },
  {
    key:"ivr", label:"IVR / Voice", Icon:Phone,
    requests:8920, resolved:8240, queued:38, avgWait:"28s",
    uptime:99.5, satisfaction:4.2,
    color:"text-violet-600", bg:"bg-violet-50", border:"border-violet-200", fill:"#7C3AED",
  },
  {
    key:"helpdesk", label:"Help Desk", Icon:Headphones,
    requests:3240, resolved:3020, queued:14, avgWait:"4 min",
    uptime:100, satisfaction:4.8,
    color:"text-amber-600", bg:"bg-amber-50", border:"border-amber-200", fill:"#F59E0B",
  },
];

const HOURLY_VOLUME = [
  { hour:"05:00", whatsapp:420,  sms:280,  ivr:180,  helpdesk:40  },
  { hour:"06:00", hour1:"06:00", whatsapp:1240, sms:820,  ivr:540,  helpdesk:120 },
  { hour:"07:00", whatsapp:2840, sms:1820, ivr:1240, helpdesk:280 },
  { hour:"08:00", whatsapp:4620, sms:2940, ivr:1980, helpdesk:440 },
  { hour:"09:00", whatsapp:5820, sms:3680, ivr:2480, helpdesk:580 },
  { hour:"10:00", whatsapp:6240, sms:4020, ivr:2720, helpdesk:640 },
  { hour:"11:00", whatsapp:5960, sms:3840, ivr:2580, helpdesk:610 },
  { hour:"12:00", whatsapp:5120, sms:3280, ivr:2180, helpdesk:520 },
  { hour:"13:00", whatsapp:4280, sms:2740, ivr:1840, helpdesk:440 },
  { hour:"14:00", whatsapp:3640, sms:2320, ivr:1560, helpdesk:370 },
];

const RESPONSE_TIME = [
  { hour:"08:00", whatsapp:45, sms:92,  ivr:68  },
  { hour:"09:00", whatsapp:62, sms:110, ivr:82  },
  { hour:"10:00", whatsapp:78, sms:134, ivr:98  },
  { hour:"11:00", whatsapp:95, sms:158, ivr:112 },
  { hour:"12:00", whatsapp:88, sms:142, ivr:104 },
  { hour:"13:00", whatsapp:72, sms:118, ivr:88  },
  { hour:"14:00", whatsapp:65, sms:105, ivr:78  },
];

const LIVE_QUERIES = [
  { id:"Q-4428", lang:"मराठी",   channel:"WhatsApp", query:"रामकुंड ते पंचवटी किती वेळ लागतो?",          aiResp:"~25 min walk via Bridge Road",       confidence:94, status:"Answered", time:"14:33" },
  { id:"Q-4427", lang:"Hindi",    channel:"IVR",      query:"Parking kahan milegi Nashik Road pe?",       aiResp:"P2 zone — Nashik Road Station, 800m",confidence:91, status:"Answered", time:"14:32" },
  { id:"Q-4426", lang:"मराठी",   channel:"SMS",       query:"हरवलेल्या व्यक्तीसाठी कुठे संपर्क करावा?",  aiResp:"Helpline: 1800-266-0400 or Camp B",   confidence:97, status:"Answered", time:"14:31" },
  { id:"Q-4425", lang:"Gujarati", channel:"WhatsApp", query:"Trimbakeshwar jaava maate bus kevi rite?",   aiResp:"Bus Bay 9 — SR-07 departs at 15:10",  confidence:88, status:"Answered", time:"14:30" },
  { id:"Q-4424", lang:"English",  channel:"HelpDesk", query:"My elderly mother needs wheelchair — help?", aiResp:"Escorted to wheelchair service desk",  confidence:99, status:"Active",   time:"14:29" },
  { id:"Q-4423", lang:"Hindi",    channel:"WhatsApp", query:"Bhojan kahan milega near Ramkund?",          aiResp:"Food Zone A — Gate 3 entry, 150m",    confidence:93, status:"Answered", time:"14:28" },
  { id:"Q-4422", lang:"मराठी",   channel:"SMS",       query:"पाण्याची सोय कुठे आहे?",                    aiResp:"Water Point W-04 — 300m north Gate 2",confidence:96, status:"Answered", time:"14:27" },
  { id:"Q-4421", lang:"मराठी",   channel:"WhatsApp", query:"रामकुंड घाटावर जाण्याचा मार्ग कोणता?",     aiResp:"Gate 4 via Godavari Bridge — 800m",   confidence:94, status:"Answered", time:"14:26" },
];

const ZONE_ADVISORIES = [
  {
    zone:"Ramkund Ghat", risk:"Critical",
    crowd:"9.2/m² — overcrowded",
    route:"Use Gate 4. Avoid Gate 2. Bridge Road alternate.",
    medical:"Camp Alpha nearby — 200m Gate 4",
    transport:"No parking — use Park & Ride at NH3",
    water:"W-01, W-02 near Gate 3 (ORS available)",
    darshan:"Wait: 90 min · Enter via Gate 4 only",
    lang:["मराठी","Hindi","English"],
  },
  {
    zone:"Panchvati", risk:"High",
    crowd:"8.1/m² — busy",
    route:"Enter from South Bridge. Avoid Ghat Steps 11:00–15:00.",
    medical:"Camp Beta — Gate 6 · Free ORS available",
    transport:"Shuttle SP-02 every 15 min · Bay 4",
    water:"W-05 to W-09 near Ghat Steps",
    darshan:"Accessible · Wait: 30 min",
    lang:["मराठी","Hindi"],
  },
  {
    zone:"Trimbakeshwar", risk:"Moderate",
    crowd:"7.6/m² — manageable",
    route:"NH3 → Trimbak Road. Free parking available.",
    medical:"Camp Gamma — Near Main Temple gate",
    transport:"Bus TR-04, TR-05 from Nashik every 30 min",
    water:"W-12 at Temple entry · Drinking water free",
    darshan:"05:00–23:00 · Wait: 45 min",
    lang:["मराठी","Hindi","Gujarati"],
  },
  {
    zone:"Nashik Road Stn", risk:"Moderate",
    crowd:"6.8/m² — normal",
    route:"Station exit → Auto/Bus to Ramkund (3 km)",
    medical:"Camp Delta — Station forecourt",
    transport:"Trains on time · Next 14:42 Platf. 2",
    water:"W-20 at Station forecourt",
    darshan:"Buses available to all ghats every 10 min",
    lang:["मराठी","Hindi","English","Gujarati"],
  },
  {
    zone:"Kalaram Temple", risk:"Low",
    crowd:"5.8/m² — comfortable",
    route:"Via Panchvati Road · Easy walk from Godavari Ghat",
    medical:"Camp Epsilon — Temple west side",
    transport:"Parking available P-4",
    water:"W-18 near Temple east gate",
    darshan:"Open · Wait: 15 min",
    lang:["मराठी","Hindi"],
  },
  {
    zone:"Gangapur Camp", risk:"Low",
    crowd:"4.1/m² — relaxed",
    route:"NH3 → Gangapur Road · Ample space",
    medical:"Camp Zeta — Fully operational",
    transport:"Free shuttle from Nashik Road",
    water:"Ample supply · W-22 to W-26",
    darshan:"N/A — Shelter zone",
    lang:["मराठी","Hindi"],
  },
];

const ROUTE_CARDS = [
  { from:"Nashik Road Station", to:"Ramkund Ghat",    walk:"35 min", bus:"15 min", auto:"12 min", busNo:"SR-01", crowd:"High", tip:"Take Gate 4 — Gate 2 is closed" },
  { from:"Gadkari Chowk",       to:"Panchvati Ghat",  walk:"18 min", bus:"8 min",  auto:"6 min",  busNo:"SP-02", crowd:"Moderate", tip:"Enter from South Bridge — less crowd" },
  { from:"Nashik Road Station", to:"Trimbakeshwar",   walk:"N/A",    bus:"42 min", auto:"35 min", busNo:"TR-04", crowd:"Moderate", tip:"Book early — limited auto after 14:00" },
  { from:"Panchvati",           to:"Kalaram Temple",  walk:"12 min", bus:"5 min",  auto:"4 min",  busNo:"KT-01", crowd:"Low", tip:"Accessible path — good for elderly" },
  { from:"Any Zone",            to:"Help & Lost+Found",walk:"—",     bus:"—",      auto:"—",      busNo:"—",     crowd:"—",  tip:"Call 1800-266-0400 · Camp B Panchvati · Gate 6" },
];

const AI_TRANSLATION = [
  { lang:"मराठी (Marathi)", accuracy:97.2, corrections:2.1, queries:18240, color:"#F59E0B", flag:"🇮🇳" },
  { lang:"हिंदी (Hindi)",   accuracy:95.8, corrections:3.4, queries:13840, color:"#2563EB", flag:"🇮🇳" },
  { lang:"English",          accuracy:98.4, corrections:1.2, queries:5180,  color:"#7C3AED", flag:"🌐" },
  { lang:"ગુજરાતી (Gujarati)",accuracy:93.6,corrections:4.8, queries:3920,  color:"#10B981", flag:"🇮🇳" },
  { lang:"Other Regional",   accuracy:88.4, corrections:8.2, queries:2180,  color:"#64748B", flag:"🗺️" },
];

const IVR_MENU = [
  { option:"1 — Route Guidance",        calls:2840, pct:32, completed:88 },
  { option:"2 — Medical Assistance",    calls:1980, pct:22, completed:94 },
  { option:"3 — Transport / Buses",     calls:1640, pct:18, completed:86 },
  { option:"4 — Missing Person Report", calls:1120, pct:13, completed:91 },
  { option:"5 — Shelter / Food",        calls:890,  pct:10, completed:82 },
  { option:"0 — Human Agent",           calls:450,  pct:5,  completed:100},
];

const BROADCAST_REACH = [
  { id:"ADV-114", type:"Route",     lang:"मराठी/Hindi", reach:82400, open:68200, channel:"WhatsApp+SMS", time:"14:20", verified:true  },
  { id:"ADV-113", type:"Medical",   lang:"मराठी",       reach:64200, open:52800, channel:"WhatsApp",     time:"14:10", verified:true  },
  { id:"ADV-112", type:"Transport", lang:"Hindi/मराठी", reach:44100, open:36800, channel:"SMS",          time:"13:58", verified:true  },
  { id:"ADV-111", type:"Safety",    lang:"All",          reach:124000,open:98400, channel:"All channels", time:"13:30", verified:true  },
  { id:"ADV-110", type:"Missing",   lang:"मराठी/Hindi", reach:22000, open:18200, channel:"WhatsApp+IVR", time:"14:12", verified:true  },
];

const SATISFACTION_DATA = [
  { hour:"08:00", score:4.6 }, { hour:"09:00", score:4.5 },
  { hour:"10:00", score:4.3 }, { hour:"11:00", score:4.2 },
  { hour:"12:00", score:4.4 }, { hour:"13:00", score:4.5 },
  { hour:"14:00", score:4.6 },
];

const RESOLUTION_PIE = [
  { name:"AI Auto-resolved", value:72, color:"#10B981" },
  { name:"Human Assisted",   value:21, color:"#2563EB" },
  { name:"Escalated",        value:4,  color:"#F59E0B" },
  { name:"Pending",          value:3,  color:"#EF4444" },
];

const DEVICE_SPLIT = [
  { name:"Smartphone — WhatsApp/App", value:58, color:"#2563EB" },
  { name:"Feature Phone — SMS only",  value:24, color:"#F59E0B" },
  { name:"Basic Phone — IVR/Voice",   value:12, color:"#7C3AED" },
  { name:"Walk-in Help Desk",         value:6,  color:"#10B981" },
];

// ── helpers ───────────────────────────────────────────────────────
// Zone/route risk vocabulary (Critical/High/Moderate/Low) mapped onto the
// shared STATUS buckets so risk badges/icons read identically to every
// other severity indicator in the app.
const RISK_BUCKET = { Critical: STATUS_KEY.critical, High: STATUS_KEY.serious, Moderate: STATUS_KEY.warning, Low: STATUS_KEY.good };
const riskBucket = r => RISK_BUCKET[r] || STATUS_KEY.good;
const riskBg = r => statusClasses(riskBucket(r));

const riskBorder = r =>
  r === "Critical" ? `${STATUS.critical.border} bg-red-50/30`   :
  r === "High"     ? `${STATUS.serious.border} bg-orange-50/20` :
  r === "Moderate" ? `${STATUS.warning.border} bg-amber-50/20`  : "border-slate-200";

// Broadcast type vocabulary -> closest STATUS bucket ("Missing" keeps a
// local violet override since STATUS has no violet/purple bucket).
const BROADCAST_TYPE_BUCKET = { Safety: STATUS_KEY.critical, Route: STATUS_KEY.info, Medical: STATUS_KEY.good, Transport: STATUS_KEY.warning };
const broadcastTypeClass = type =>
  type === "Missing" ? "bg-violet-100 text-violet-700" : statusClasses(BROADCAST_TYPE_BUCKET[type] || STATUS_KEY.warning);

// Route/crowd load vocabulary (High/Moderate/else) -> STATUS buckets.
const CROWD_BUCKET = { High: STATUS_KEY.critical, Moderate: STATUS_KEY.warning };
const crowdClass = crowd => statusClasses(CROWD_BUCKET[crowd] || STATUS_KEY.good);

// Every zone this page advises pilgrims about also has a live density/risk
// read on Crowd Intelligence — link to it instead of leaving it a dead label.
const ZONE_SLUG = {
  "Ramkund Ghat":"Ramkund", "Panchvati":"Panchvati", "Trimbakeshwar":"Trimbak",
  "Nashik Road Stn":"Nashik Rd", "Kalaram Temple":"Kalaram", "Gangapur Camp":"Gangapur",
};
const ZoneLink = ({ zone, children }) => {
  const slug = ZONE_SLUG[zone];
  if (!slug) return <>{children}</>;
  return (
    <Link to={`/crowd-intelligence?zone=${encodeURIComponent(slug)}`}
      onClick={e => e.stopPropagation()}
      className="hover:text-blue-700 hover:underline decoration-dotted underline-offset-2">
      {children}
    </Link>
  );
};

// ── component ─────────────────────────────────────────────────────
export default function PilgrimSupport() {
  const { live: liveModel, twin } = useIntelligence({ running: false });
  const t = useTranslate();
  const [zoneExpanded, setZoneExpanded] = useState(null);
  const [activeTab, setActiveTab]       = useState("queries");
  const [broadcastTab, setBroadcastTab] = useState("advisories");

  // Computed from the real arrays rendered below, not hand-typed — so these
  // can't silently drift from the channel cards / pie / IVR table underneath.
  const totalRequests   = CHANNEL_DETAIL.reduce((s, c) => s + c.requests, 0);
  const totalResolved   = CHANNEL_DETAIL.reduce((s, c) => s + c.resolved, 0);
  const totalQueued     = CHANNEL_DETAIL.reduce((s, c) => s + c.queued, 0);
  const aiResolutionPct = RESOLUTION_PIE.find(r => r.name === "AI Auto-resolved")?.value ?? 0;
  const avgSatisfaction = (
    SATISFACTION_DATA.reduce((s, d) => s + d.score, 0) / SATISFACTION_DATA.length
  ).toFixed(1);
  const totalIvrCalls   = IVR_MENU.reduce((s, m) => s + m.calls, 0);
  const ivrCompletionPct = Math.round(
    IVR_MENU.reduce((s, m) => s + m.calls * m.completed, 0) / totalIvrCalls
  );
  // pilgrimAdvisories (shared mockData) carries the actual advisory text;
  // BROADCAST_REACH (local) carries the delivery/open-rate metrics for the
  // same IDs — join them so the reach card shows what was actually said.
  const advisoryMessage = id => pilgrimAdvisories.find(a => a.id === id)?.message;

  return (
    <div className="space-y-8">
      <SectionHeader icon="MessageCircle" title="Pilgrim Guidance Layer"
        subtitle="360° pilgrim support · multilingual AI · route guidance · zone advisories · IVR analytics · broadcast performance · satisfaction tracking · digital-divide coverage"
      />


      <LivePilgrimSection live={liveModel} twin={twin} />

      <StaticContentDivider />
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard title="Total Requests"   value={totalRequests.toLocaleString()}  icon="MessageCircle" color="blue"   index={0} live />
        <MetricCard title="Resolved Today"   value={totalResolved.toLocaleString()}  icon="CheckCircle"   color="green"  index={1} />
        <MetricCard title="Active Queries"   value={totalQueued.toLocaleString()}    icon="Zap"           color="amber"  index={2} live />
        <MetricCard title="Broadcasts Live"  value={BROADCAST_REACH.length.toString()} icon="Radio"       color="purple" index={3} />
        <MetricCard title="AI Resolution"    value={`${aiResolutionPct}%`}           icon="Brain"         color="green"  index={4} />
        <MetricCard title="Satisfaction"     value={`${avgSatisfaction} / 5`}        icon="Star"          color="amber"  index={5} />
      </div>

      {/* System Status Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("ALL CHANNELS OPERATIONAL")} — 14:33 IST</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Maha Kumbh AI Pilgrim Guidance Engine — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {t("Marathi-first multilingual AI · WhatsApp · SMS · IVR · Help Desk · 43,180 queries handled today · 72% AI auto-resolved · 4 channels live")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {CHANNEL_DETAIL.map((ch, i) => {
              const Icon = ch.Icon;
              const c = googleColor(i);
              return (
                <div key={ch.key} className="rounded-xl px-3 py-2 text-center min-w-[90px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Icon size={12} style={{ color:c.text }} />
                    <span className="text-[9px]" style={{ color:c.text }}>{t(ch.label)}</span>
                  </div>
                  <div className="text-base font-black" style={{ color:c.text }}>{ch.requests.toLocaleString()}</div>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                    <span className="text-[9px] text-emerald-600">{ch.uptime}% {t("up")}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Channel Detail Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CHANNEL_DETAIL.map((ch, i) => {
          const Icon = ch.Icon;
          return (
            <motion.div key={ch.key}
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
              className={`card p-4 border ${ch.border}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 ${ch.bg} rounded-xl flex items-center justify-center`}>
                  <Icon size={18} className={ch.color} />
                </div>
                <div className="flex items-center gap-1 text-[9px] text-emerald-600 font-bold">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                  {ch.uptime}%
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900">{ch.requests.toLocaleString()}</div>
              <div className="text-xs font-bold text-slate-500 mb-2">{t(ch.label)}</div>
              <div className="grid grid-cols-2 gap-1 text-[9px]">
                <div className="bg-slate-50 rounded-lg p-1.5 text-center">
                  <div className="font-black text-emerald-600">{ch.queued}</div>
                  <div className="text-slate-400">{t("In Queue")}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-1.5 text-center">
                  <div className="font-black text-blue-600">{ch.avgWait}</div>
                  <div className="text-slate-400">{t("Avg Wait")}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1 text-[9px]">
                <Star size={9} className="text-amber-500" />
                <span className="font-bold text-slate-700">{ch.satisfaction}</span>
                <span className="text-slate-400">{t("satisfaction")}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Request Volume + Resolution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartCard title="Request Volume — Hourly by Channel"
            subtitle="Incoming pilgrim queries across all 4 channels throughout the day" live>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={HOURLY_VOLUME} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <defs>
                  {[
                    { id:"waGrad", color:chartColor(0) }, { id:"smsGrad", color:chartColor(1) },
                    { id:"ivrGrad",color:chartColor(2) }, { id:"hdGrad",  color:chartColor(3) },
                  ].map(g => (
                    <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={g.color} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={g.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="hour" tick={axisTick} />
                <YAxis tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
                <Tooltip content={<ChartTooltip formatter={(v, n) => [v?.toLocaleString(), n]} />} />
                <Legend wrapperStyle={legendStyle} />
                <Area type="monotone" dataKey="whatsapp" fill="url(#waGrad)"  stroke={chartColor(0)} strokeWidth={2} name="WhatsApp" />
                <Area type="monotone" dataKey="sms"      fill="url(#smsGrad)" stroke={chartColor(1)} strokeWidth={2} name={t("SMS")} />
                <Area type="monotone" dataKey="ivr"      fill="url(#ivrGrad)" stroke={chartColor(2)} strokeWidth={2} name="IVR" />
                <Area type="monotone" dataKey="helpdesk" fill="url(#hdGrad)"  stroke={chartColor(3)} strokeWidth={2} name={t("HelpDesk")} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Query Resolution Breakdown">
          <div className="flex items-center gap-2 mb-2">
            <ResponsiveContainer width="55%" height={180}>
              <PieChart>
                <Pie data={RESOLUTION_PIE} dataKey="value" cx="50%" cy="50%"
                  innerRadius={45} outerRadius={80} label={({ value })=>`${value}%`} labelLine={false}>
                  {RESOLUTION_PIE.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip formatter={(v, n) => [`${v}%`, n]} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 flex-1">
              {RESOLUTION_PIE.map((r, i) => (
                <div key={r.name} className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-[10px] text-slate-600 leading-tight">{t(r.name)}</span>
                  </div>
                  <span className="text-xs font-black text-slate-800">{r.value}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 text-center text-[10px]">
            <div><div className="text-lg font-black text-blue-700">{totalResolved.toLocaleString()}</div><div className="text-slate-500">{t("Resolved")}</div></div>
            <div><div className="text-lg font-black text-amber-600">{totalQueued.toLocaleString()}</div><div className="text-slate-500">{t("Active")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Multilingual Banner */}
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
        className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Globe size={16} className="text-amber-600" />
              <h3 className="text-sm font-black text-slate-900">{t("Marathi-First Multilingual AI Guidance Engine")}</h3>
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed max-w-2xl">
              {t("Maha Kumbh AI operates Marathi as primary (42%). AI NLP translates in real-time across 5 languages. Feature-phone pilgrims receive Unicode SMS in local script. IVR offers regional dialect support. No internet required for SMS or IVR channels — full digital-divide coverage.")}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {["मराठी — Primary (42%)","Hindi (32%)","English (12%)","ગુજરાતી (9%)","Regional (5%)"].map(l => (
                <span key={l} className="badge badge-warning text-[9px]">{t(l)}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            {[
              { label:"Languages",    val:"5",    color:"text-amber-700" },
              { label:"Dialects",     val:"12",   color:"text-amber-700" },
              { label:"AI Accuracy",  val:"95.8%",color:"text-emerald-700"},
              { label:"No-Internet",  val:"36%",  color:"text-blue-700"  },
            ].map(s => (
              <div key={s.label} className="bg-white/70 border border-amber-200 rounded-xl px-3 py-2">
                <div className={`text-base font-black ${s.color}`}>{s.val}</div>
                <div className="text-[9px] text-slate-500">{t(s.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Language + Categories + AI Translation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ChartCard title="Language Distribution">
          <div className="flex items-center gap-3">
            <ResponsiveContainer width="55%" height={180}>
              <PieChart>
                <Pie data={pilgrimGuidance.languages} dataKey="value" cx="50%" cy="50%"
                  outerRadius={78} innerRadius={38} label={({value})=>`${value}%`} labelLine={false}>
                  {pilgrimGuidance.languages.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 flex-1">
              {pilgrimGuidance.languages.map((l, i) => (
                <div key={l.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background:chartColor(i) }} />
                    <span className="text-xs text-slate-700">{t(l.name)}</span>
                  </div>
                  <span className="text-sm font-black text-slate-900">{l.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Request Categories">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={pilgrimGuidance.categories} layout="vertical" margin={{ left:8, right:44 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={axisTick} width={110} tickFormatter={v => t(v)} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" fill={chartColor(0)} radius={[0,4,4,0]}
                label={{ position:"right", fontSize:9, fill:"#64748B", formatter:v=>v.toLocaleString() }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="AI Translation Accuracy">
          <div className="space-y-3">
            {AI_TRANSLATION.map((l, i) => (
              <motion.div key={l.lang}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-slate-700 truncate flex-1 mr-2">{t(l.lang)}</span>
                  <span className="text-xs font-black" style={{ color: l.color }}>{l.accuracy}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-2 rounded-full" style={{ background: l.color }}
                    initial={{ width:0 }} animate={{ width:`${l.accuracy}%` }}
                    transition={{ duration:0.8, delay: i * 0.06 }} />
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
                  <span>{l.queries.toLocaleString()} {t("queries")}</span>
                  <span>{l.corrections}% {t("human corrections")}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Live Queries + Response Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Live Query Feed"
          subtitle="Real-time AI-processed pilgrim inquiries — all channels" live>
          <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-0.5">
            {LIVE_QUERIES.map((q, i) => (
              <motion.div key={q.id}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.04 }}
                className={`p-2.5 rounded-xl border text-xs ${
                  q.status === "Active" ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="font-black text-blue-700 font-mono text-[9px]">{q.id}</span>
                    <span className="badge badge-warning text-[8px]">{t(q.lang)}</span>
                    <span className="badge badge-info text-[8px]">{t(q.channel)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] text-violet-600 font-bold">AI:{q.confidence}%</span>
                    <span className="text-[9px] text-slate-400 font-mono">{q.time}</span>
                    <span className={`text-[8px] font-black px-1 py-0.5 rounded-full ${
                      statusClasses(q.status === "Active" ? STATUS_KEY.warning : STATUS_KEY.good)
                    }`}>{t(q.status)}</span>
                  </div>
                </div>
                <p className="text-slate-700 font-semibold text-[10px] mb-0.5">{t(q.query)}</p>
                <p className="text-blue-700 text-[10px]">→ {t(q.aiResp)}</p>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Channel Response Time — Hourly Average"
          subtitle="Response time (seconds) across WhatsApp, SMS, IVR channels" live>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={RESPONSE_TIME} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="hour" tick={axisTick} />
              <YAxis tick={axisTick} unit="s" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="whatsapp" stroke={chartColor(0)} strokeWidth={2.5} dot={{ r:3 }} name="WhatsApp (s)" />
              <Line type="monotone" dataKey="sms"      stroke={chartColor(1)} strokeWidth={2.5} dot={{ r:3 }} name="SMS (s)" />
              <Line type="monotone" dataKey="ivr"      stroke={chartColor(2)} strokeWidth={2.5} dot={{ r:3 }} name="IVR (s)" />
            </LineChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-lg font-black text-emerald-600">12s</div><div className="text-[10px] text-slate-500">{t("WhatsApp avg")}</div></div>
            <div><div className="text-lg font-black text-blue-600">45s</div><div className="text-[10px] text-slate-500">{t("SMS avg")}</div></div>
            <div><div className="text-lg font-black text-violet-600">28s</div><div className="text-[10px] text-slate-500">{t("IVR avg")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Zone Advisory Board */}
      <div>
        <h3 className="text-sm font-black text-slate-900 mb-4">{t("Zone-wise Pilgrim Advisory Board")}</h3>
        <div className="space-y-2">
          {ZONE_ADVISORIES.map((z, i) => {
            const isOpen = zoneExpanded === z.zone;
            return (
              <motion.div key={z.zone}
                initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
                className={`rounded-2xl border transition-all ${riskBorder(z.risk)} ${isOpen ? "shadow-card-hover" : ""}`}
              >
                <div className="p-4 cursor-pointer flex items-center justify-between gap-3"
                  onClick={() => setZoneExpanded(isOpen ? null : z.zone)}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${STATUS[riskBucket(z.risk)].bg}`}>
                      <MapPin size={14} className={STATUS[riskBucket(z.risk)].text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-black text-slate-900"><ZoneLink zone={z.zone}>{t(z.zone)}</ZoneLink></span>
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${riskBg(z.risk)}`}>{t(z.risk)}</span>
                        <span className="text-[10px] text-slate-500">{t(z.crowd)}</span>
                      </div>
                      <div className="flex gap-1 mt-0.5 flex-wrap">
                        {z.lang.map(l => <span key={l} className="badge badge-warning text-[8px]">{t(l)}</span>)}
                      </div>
                    </div>
                  </div>
                  {isOpen ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" /> :
                             <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                      exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
                      className="overflow-hidden border-t border-slate-200">
                      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white/60 rounded-b-2xl">
                        {[
                          { icon:Navigation, label:"Route",    val:z.route,     color:"text-blue-600",    bg:"bg-blue-50"    },
                          { icon:Heart,      label:"Medical",  val:z.medical,   color:"text-red-600",     bg:"bg-red-50"     },
                          { icon:MessageCircle,label:"Transport",val:z.transport,color:"text-amber-600",  bg:"bg-amber-50"   },
                          { icon:Zap,        label:"Water",    val:z.water,     color:"text-sky-600",     bg:"bg-sky-50"     },
                          { icon:Clock,      label:"Darshan",  val:z.darshan,   color:"text-violet-600",  bg:"bg-violet-50"  },
                          { icon:Users,      label:"Crowd",    val:z.crowd,     color:"text-slate-600",   bg:"bg-slate-50"   },
                        ].map(d => {
                          const Icon = d.icon;
                          return (
                            <div key={d.label} className={`p-2.5 rounded-xl border border-slate-200 ${d.bg}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Icon size={11} className={d.color} />
                                <span className="text-[9px] font-black text-slate-500 uppercase">{t(d.label)}</span>
                              </div>
                              <p className="text-[10px] text-slate-700 font-semibold leading-snug">{t(d.val)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Route Cards + Broadcasts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Pilgrim Route Guidance Cards"
          subtitle="AI-generated simple directions for most-requested destinations">
          <div className="space-y-2">
            {ROUTE_CARDS.map((r, i) => (
              <motion.div key={i}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className="p-3 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-card-hover transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[9px] text-slate-500">{t(r.from)}</span>
                      <span className="text-white/60">→</span>
                      <span className="text-xs font-black text-blue-700">{t(r.to)}</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {r.walk !== "N/A" && r.walk !== "—" && (
                        <span className="text-[9px] bg-slate-100 px-1.5 py-0.5 rounded-full text-slate-600">🚶 {r.walk}</span>
                      )}
                      {r.bus !== "—" && (
                        <span className="text-[9px] bg-blue-50 px-1.5 py-0.5 rounded-full text-blue-600">🚌 {r.bus} ({r.busNo})</span>
                      )}
                      {r.auto !== "—" && (
                        <span className="text-[9px] bg-amber-50 px-1.5 py-0.5 rounded-full text-amber-600">🛺 {r.auto}</span>
                      )}
                    </div>
                  </div>
                  {r.crowd !== "—" && (
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0 ${crowdClass(r.crowd)}`}>{t(r.crowd)}</span>
                  )}
                </div>
                <p className="text-[9px] text-blue-700 font-semibold mt-1.5">ℹ {t(r.tip)}</p>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        {/* Broadcasts + Missing Person tabs */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black text-slate-900">{t("Live Broadcasts & Advisories")}</h3>
            <div className="flex gap-1">
              {[{id:"advisories",label:"Broadcasts"},{id:"missing",label:"MP Links"}].map(tab => (
                <button key={tab.id} onClick={() => setBroadcastTab(tab.id)}
                  className={`text-[9px] font-black px-2.5 py-1 rounded-lg transition-colors ${
                    broadcastTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                  }`}>{t(tab.label)}</button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {broadcastTab === "advisories" && (
              <motion.div key="adv" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                className="space-y-2">
                {BROADCAST_REACH.map((b, i) => (
                  <motion.div key={b.id}
                    initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                    className="p-3 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[9px] font-mono text-slate-400">{b.id}</span>
                          <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${broadcastTypeClass(b.type)}`}>{t(b.type)}</span>
                          <span className="badge badge-warning text-[8px]">{t(b.lang)}</span>
                          {b.verified && <CheckCircle size={9} className="text-emerald-500" />}
                        </div>
                        <div className="text-[9px] text-slate-500">{t(b.channel)} · {b.time}</div>
                      </div>
                      <div className="text-right text-[10px]">
                        <div className="font-black text-slate-900">{b.reach.toLocaleString()}</div>
                        <div className="text-slate-400">{t("reach")}</div>
                      </div>
                    </div>
                    {advisoryMessage(b.id) && (
                      <p className="text-[10px] text-slate-600 leading-snug mb-1.5">{t(advisoryMessage(b.id))}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100">
                        <div className="h-1.5 rounded-full bg-emerald-500" style={{ width:`${Math.round(b.open/b.reach*100)}%` }} />
                      </div>
                      <span className="text-[9px] text-slate-500 flex-shrink-0">{Math.round(b.open/b.reach*100)}% {t("opened")}</span>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
            {broadcastTab === "missing" && (
              <motion.div key="mp" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                className="space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3 text-center text-xs">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-2">
                    <div className="text-xl font-black text-red-600">{missingPersonData.total}</div>
                    <div className="text-[9px] text-slate-500">{t("Total Cases")}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2">
                    <div className="text-xl font-black text-emerald-600">{missingPersonData.resolved}</div>
                    <div className="text-[9px] text-slate-500">{t("Resolved")}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-2">
                    <div className="text-xl font-black text-amber-600">{missingPersonData.total - missingPersonData.resolved}</div>
                    <div className="text-[9px] text-slate-500">{t("Active")}</div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {missingPersonData.cases.filter(c => c.status === "Active").map((c, i) => (
                    <div key={c.id} className="flex items-center gap-2 p-2.5 rounded-xl border border-amber-200 bg-amber-50/30">
                      <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Users size={12} className="text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-black text-slate-800">{t(c.name)}</div>
                        <div className="text-[9px] text-slate-500 truncate"><MapPin size={8} className="inline" /> {t(c.zone)}</div>
                      </div>
                      <div className="text-right text-[9px]">
                        <div className="font-bold text-slate-600">{t(c.assignee)}</div>
                        <div className="text-slate-400">{c.reportedAt}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] text-blue-700 font-bold mt-2 text-center">
                  {t("Missing Person Helpline: 1800-266-0400 · Camp B Gate 6 · WhatsApp: +91-8888-266-400")}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* IVR Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="IVR Call Analytics — Menu Choice Distribution"
          subtitle="How pilgrims are navigating the IVR menu · completion rate per option">
          <div className="space-y-2.5">
            {IVR_MENU.map((m, i) => (
              <motion.div key={m.option}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
              >
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="text-[10px] font-semibold text-slate-700 flex-1">{t(m.option)}</span>
                  <div className="flex items-center gap-2 flex-shrink-0 text-[10px]">
                    <span className="font-black text-slate-800">{m.calls.toLocaleString()}</span>
                    <span className="text-slate-400">{m.pct}%</span>
                  </div>
                </div>
                <div className="flex gap-1 items-center">
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className="h-2.5 rounded-full bg-violet-500"
                      initial={{ width:0 }} animate={{ width:`${m.pct * 2.5}%` }}
                      transition={{ duration:0.8, delay: i * 0.06 }} />
                  </div>
                  <span className="text-[9px] text-emerald-600 font-bold w-10 text-right flex-shrink-0">{m.completed}% {t("done")}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-base font-black text-violet-600">{totalIvrCalls.toLocaleString()}</div><div className="text-[10px] text-slate-500">{t("Total IVR Calls")}</div></div>
            <div><div className="text-base font-black text-emerald-600">{ivrCompletionPct}%</div><div className="text-[10px] text-slate-500">{t("Completion Rate")}</div></div>
            <div><div className="text-base font-black text-amber-600">28s</div><div className="text-[10px] text-slate-500">{t("Avg Call Duration")}</div></div>
          </div>
        </ChartCard>

        {/* Satisfaction + Device Split */}
        <div className="space-y-4">
          <ChartCard title="Pilgrim Satisfaction Trend"
            subtitle="Hourly satisfaction score across all channels">
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={SATISFACTION_DATA} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="hour" tick={{ ...axisTick, fontSize:9 }} />
                <YAxis domain={[4, 5]} tick={{ ...axisTick, fontSize:9 }} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="score" stroke={chartColor(0)} strokeWidth={2.5} dot={{ r:3 }} name={t("Satisfaction")} />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-1 mt-1">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={14} className={s <= Math.round(avgSatisfaction) ? "text-amber-400 fill-amber-400" : "text-amber-300 fill-amber-100"} />
              ))}
              <span className="text-sm font-black text-slate-800 ml-1">{avgSatisfaction} / 5.0</span>
            </div>
          </ChartCard>

          <ChartCard title="Device & Access Channel Split"
            subtitle="Connectivity profile of pilgrims using guidance system">
            <div className="flex items-center gap-3">
              <ResponsiveContainer width="45%" height={120}>
                <PieChart>
                  <Pie data={DEVICE_SPLIT} dataKey="value" cx="50%" cy="50%"
                    innerRadius={30} outerRadius={55} label={({value})=>`${value}%`} labelLine={false}>
                    {DEVICE_SPLIT.map((d, i) => <Cell key={i} fill={chartColor(i)} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={(v, n) => [`${v}%`, n]} />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 flex-1">
                {DEVICE_SPLIT.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                    <span className="text-[9px] text-slate-600 flex-1 leading-tight">{t(d.name)}</span>
                    <span className="text-[10px] font-black text-slate-800">{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}






